import PropTypes from 'prop-types';
import ReactSelect from 'react-select';
import classNames from 'classnames';
import SingleValue from './SingleValue/SingleValue';
import Option from './Option/Option';

import './Select.scss';

const types = [ 'tall', 'borderless' ];

const preventEventIfSpacebar = event => event.code === 'Space' && event.preventDefault();

const NoOptionsMessage = () => <p className="empty-selection">No options</p>;

const CustomSingleValue = ( { selectProps, ...props } ) => (
	<SingleValue
		{...props}
		selectProps={selectProps}
		greyOut
		showSelectedDot={selectProps.showSelectedDot}
		displayValueFormatter={selectProps.displayValueFormatter}
		selectedOptionType={selectProps.selectedOptionType}
	/>
);

CustomSingleValue.propTypes = {
	selectProps: PropTypes.shape( {
		showSelectedDot: PropTypes.bool,
		displayValueFormatter: PropTypes.func,
		selectedOptionType: PropTypes.string
	} ).isRequired
};

const CustomOption = ( { selectProps, ...props } ) => (
	<Option {...props} selectProps={selectProps} withDescription={selectProps.withDescription} />
);

CustomOption.propTypes = {
	selectProps: PropTypes.shape( {
		withDescription: PropTypes.bool
	} ).isRequired
};

const Select = ( {
	initialValue, options, placeholder, showSelectedDot,
	selected, onChange, withDescription, displayValueFormatter,
	label, type, informationTooltip, disabled, isSearchable,
	required, withButtonOption, invalidSelection, error,
	willShowError, selectedOptionType, preventSpacebarOpening,
	keepOpenOnSelect, customOption, onMenuOpen, onMenuClose
} ) => {
	const selectComponents = {
		SingleValue: CustomSingleValue,
		Option: customOption || CustomOption,
		NoOptionsMessage
	};

	return (
		<div className={classNames( 'Select', {
			[ type ]: type, withDescription, withButtonOption, invalidSelection, error: !!error
		} )}
		>
			<div className="select-header">
				{!!label && (
					<h4>
						{label}
						{required && <span>*</span>}
					</h4>
				)}
				{informationTooltip}
			</div>
			<ReactSelect
				defaultValue={initialValue}
				value={selected}
				placeholder={placeholder}
				options={options}
				onChange={onChange}
				isDisabled={disabled}
				isSearchable={isSearchable}
				closeMenuOnSelect={!keepOpenOnSelect}
				onMenuOpen={onMenuOpen}
				onMenuClose={onMenuClose}
				components={selectComponents}
				classNamePrefix="Select"
				aria-label={label || 'Select'}
				onKeyDown={preventSpacebarOpening ? preventEventIfSpacebar : undefined}
				showSelectedDot={showSelectedDot}
				displayValueFormatter={displayValueFormatter}
				selectedOptionType={selectedOptionType}
				withDescription={withDescription}
			/>
			{willShowError
			&& (
				<p className={classNames( 'error-message', { visible: error && typeof error === 'string' } )}>
					{invalidSelection && error}
				</p>
			) }
		</div>
	);
};

Select.propTypes = {
	initialValue: PropTypes.oneOfType( [
		PropTypes.shape( {
			label: PropTypes.string.isRequired,
			caption: PropTypes.string,
			options: PropTypes.arrayOf( PropTypes.any )
		} ),
		PropTypes.any
	] ),
	options: PropTypes.oneOfType( [
		PropTypes.arrayOf(
			PropTypes.shape( {
				label: PropTypes.string.isRequired,
				caption: PropTypes.string,
				options: PropTypes.arrayOf( PropTypes.any )
			} )
		),
		PropTypes.arrayOf( PropTypes.any )
	] ),
	selected: PropTypes.any,
	placeholder: PropTypes.string,
	displayValueFormatter: PropTypes.func,
	onChange: PropTypes.func,
	withDescription: PropTypes.bool,
	showSelectedDot: PropTypes.bool,
	label: PropTypes.string,
	type: PropTypes.oneOf( types ),
	informationTooltip: PropTypes.element,
	disabled: PropTypes.bool,
	isSearchable: PropTypes.bool,
	required: PropTypes.bool,
	withButtonOption: PropTypes.bool,
	invalidSelection: PropTypes.bool,
	error: PropTypes.oneOfType( [ PropTypes.bool, PropTypes.string ] ),
	willShowError: PropTypes.bool,
	selectedOptionType: PropTypes.string,
	preventSpacebarOpening: PropTypes.bool,
	keepOpenOnSelect: PropTypes.bool,
	customOption: PropTypes.func,
	onMenuOpen: PropTypes.func,
	onMenuClose: PropTypes.func
};

Select.defaultProps = {
	initialValue: undefined,
	options: [],
	selected: undefined,
	placeholder: 'Select...',
	displayValueFormatter: label => label,
	onChange: () => {},
	withDescription: false,
	showSelectedDot: false,
	label: undefined,
	type: undefined,
	informationTooltip: undefined,
	disabled: false,
	isSearchable: false,
	required: false,
	withButtonOption: false,
	invalidSelection: false,
	error: false,
	willShowError: false,
	selectedOptionType: undefined,
	preventSpacebarOpening: false,
	keepOpenOnSelect: false,
	customOption: undefined,
	onMenuOpen: undefined,
	onMenuClose: undefined
};

export default Select;
